import type { HitProps } from '@components/algolia/Hit';

const MAX_PRODUCTS = 12;
const PRODUCT_SLOT_NUMBERS = Array.from(
  { length: MAX_PRODUCTS },
  (_, i) => i + 1
);

export interface ImageProductStripPrimary {
  heading?: string | null;
  co_op_brand?: string | null;
  desktop_image?: { url?: string | null; alt?: string | null } | null;
  mobile_image?: { url?: string | null; alt?: string | null } | null;
  strip_heading?: string | null;
  [dynamicField: string]: unknown;
}

export interface ImageProductStripSlice {
  id?: string;
  slice_type?: string;
  primary?: ImageProductStripPrimary | null;
}

export interface ImageProductStripImage {
  url: string;
  alt: string | null;
}

export interface ImageProductStripProduct {
  product: HitProps;
  variantSku: string | null;
}

export interface ImageProductStripCta {
  label: string;
  url: string;
}

export interface ImageProductStripData {
  heading: string | null;
  desktopImage: ImageProductStripImage;
  mobileImage: ImageProductStripImage;
  stripHeading: string | null;
  ctas: ImageProductStripCta[];
  products: ImageProductStripProduct[];
  coOpBrand: string | null;
}

const trimmed = (value: unknown): string | null =>
  typeof value === 'string' ? value.trim() || null : null;

const toImage = (
  image: { url?: string | null; alt?: string | null } | null | undefined
): ImageProductStripImage | null => {
  const url = trimmed(image?.url);
  return url ? { url, alt: trimmed(image?.alt) } : null;
};

const productSkuField = (n: number): string => `product_${n}_sku`;
const productVariantSkuField = (n: number): string =>
  `product_${n}_variant_sku`;

export const collectImageProductStripSkus = (
  slice: ImageProductStripSlice | null
): string[] =>
  PRODUCT_SLOT_NUMBERS.flatMap((n) => {
    const sku = trimmed(slice?.primary?.[productSkuField(n)]);
    return sku ? [sku] : [];
  });

export const collectImageProductStripVariantSkus = (
  slice: ImageProductStripSlice | null
): string[] =>
  PRODUCT_SLOT_NUMBERS.flatMap((n) => {
    const sku = trimmed(slice?.primary?.[productVariantSkuField(n)]);
    return sku ? [sku] : [];
  });

const collectProducts = (
  primary: ImageProductStripPrimary,
  resolvedProducts: Record<string, HitProps>
): ImageProductStripProduct[] => {
  const seen = new Set<string>();

  return PRODUCT_SLOT_NUMBERS.flatMap((n) => {
    const sku = trimmed(primary[productSkuField(n)]);
    const product = sku ? resolvedProducts[sku] : undefined;

    if (
      !product ||
      product.availableOnPlp === false ||
      seen.has(product.parentSku)
    ) {
      return [];
    }

    seen.add(product.parentSku);
    return [
      { product, variantSku: trimmed(primary[productVariantSkuField(n)]) },
    ];
  });
};

const collectCtas = (
  primary: ImageProductStripPrimary
): ImageProductStripCta[] =>
  ([1, 2] as const).flatMap((n) => {
    const ctaLabel = trimmed(primary[`cta_${n}_label`]);
    const ctaUrl = trimmed(primary[`cta_${n}_link`]);
    return ctaLabel && ctaUrl ? [{ label: ctaLabel, url: ctaUrl }] : [];
  });

export const transformImageProductStrip = (
  slice: ImageProductStripSlice | null,
  resolvedProducts: Record<string, HitProps> = {}
): ImageProductStripData | null => {
  const primary = slice?.primary;
  const desktopImage = toImage(primary?.desktop_image);
  const mobileImage = toImage(primary?.mobile_image);

  if (!primary || !desktopImage || !mobileImage) {
    return null;
  }

  return {
    heading: trimmed(primary.heading),
    desktopImage,
    mobileImage,
    stripHeading: trimmed(primary.strip_heading),
    ctas: collectCtas(primary),
    products: collectProducts(primary, resolvedProducts),
    coOpBrand: trimmed(primary.co_op_brand),
  };
};
